<template>
  <div class="quota-card" :class="{ switched: switchedModel }">
    <template v-if="!switchedModel">
      <div class="quota-head">
        <div class="quota-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
        </div>
        <div class="quota-title-block">
          <strong>{{ $t('browserAgentChat.quotaLimitTitle') }}</strong>
          <span>{{ $t('browserAgentChat.quotaLimitDesc', { limit }) }}</span>
        </div>
      </div>

      <div class="quota-meter">
        <div class="quota-meter-label">
          <span>{{ $t('browserAgentChat.quotaLimitUsage', { limit }) }}</span>
          <span>100%</span>
        </div>
        <div class="quota-meter-track"><i></i></div>
      </div>

      <div class="quota-upsell">{{ $t('browserAgentChat.quotaLimitUpsell') }}</div>

      <button type="button" class="quota-buy-btn" @click.stop="openStore">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/></svg>
        {{ $t('browserAgentChat.quotaLimitBuy') }}
      </button>
      <div class="quota-byok">{{ $t('browserAgentChat.quotaLimitByok') }}</div>
    </template>

    <template v-else>
      <div class="quota-head">
        <div class="quota-icon ok" aria-hidden="true">✓</div>
        <div class="quota-title-block">
          <strong>{{ $t('browserAgentChat.quotaSwitchedTitle', { name: switchedModel.name || switchedModel.model }) }}</strong>
          <span>{{ $t('browserAgentChat.quotaSwitchedDesc') }}</span>
        </div>
      </div>
      <button v-if="canAskAgain" type="button" class="quota-buy-btn ok" @click.stop="$emit('ask-again')">
        {{ $t('browserAgentChat.quotaAskAgain') }}
      </button>
    </template>

    <div ref="storePortal">
      <TokenStoreFullscreen v-if="isStoreOpen" @close="isStoreOpen = false" />
    </div>
  </div>
</template>

<script>
import { store } from '@/store';
import { syncTokenStoreModels } from '@/utils/tokenStoreSync';
import TokenStoreFullscreen from './TokenStoreFullscreen.vue';

// 与 store 中 createDefaultAgentModel / 后端 DEFAULT_*_CHECK 一致:命中即为受每日次数限制的内置模型
const BUILT_IN_ENDPOINT = 'https://api.rainsee.cn/v1/chat/completions';
const BUILT_IN_API_KEY = 'sk-aabaaaaaaaa';

export default {
  name: 'QuotaLimitCard',
  components: { TokenStoreFullscreen },
  props: {
    limit: { type: Number, default: 0 },
    canAskAgain: { type: Boolean, default: false }
  },
  data() {
    return { isStoreOpen: false };
  },
  computed: {
    // 商店购买成功后会 addModel + setActiveModelByType,当前文本模型不再是内置模型即视为已切换
    switchedModel() {
      const settings = store.settings || {};
      const models = settings.models || [];
      const active = models.find(m => m.id === settings.activeTextModelId);
      if (!active) return null;
      const isBuiltIn = active.endpoint === BUILT_IN_ENDPOINT && active.apiKey === BUILT_IN_API_KEY;
      return isBuiltIn ? null : active;
    }
  },
  mounted() {
    // 用户可能刚在浏览器设置页买过:先按账号从云端导入,命中即直接显示「已切换」
    syncTokenStoreModels();
    // 商店是 position:fixed 全屏层,挂到 body 下避免被消息列表的 overflow/transform 裁切
    document.body.appendChild(this.$refs.storePortal);
  },
  beforeDestroy() {
    const portal = this.$refs.storePortal;
    if (portal && portal.parentNode) portal.parentNode.removeChild(portal);
  },
  methods: {
    openStore() {
      this.isStoreOpen = true;
    }
  }
};
</script>

<style scoped>
.quota-card {
  margin-top: 4px;
  padding: 16px;
  border: 1px solid #FED7AA;
  border-radius: 14px;
  background: linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 100%);
  color: #1F2937;
  max-width: 420px;
}

.quota-card.switched {
  border-color: #BBF7D0;
  background: linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%);
}

.quota-head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.quota-icon {
  flex: 0 0 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFEDD5;
  color: #EA580C;
  font-weight: 700;
}

.quota-icon.ok {
  background: #DCFCE7;
  color: #16A34A;
}

.quota-title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.quota-title-block strong {
  font-size: 15px;
  line-height: 1.4;
}

.quota-title-block span {
  font-size: 13px;
  line-height: 1.55;
  color: #6B7280;
}

.quota-meter {
  margin-top: 14px;
}

.quota-meter-label {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #9A3412;
  margin-bottom: 6px;
  font-variant-numeric: tabular-nums;
}

.quota-meter-track {
  height: 6px;
  border-radius: 999px;
  background: #FFEDD5;
  overflow: hidden;
}

.quota-meter-track i {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #FB923C, #EA580C);
}

.quota-upsell {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(47, 84, 235, 0.06);
  font-size: 13px;
  line-height: 1.55;
  color: #374151;
}

.quota-buy-btn {
  margin-top: 12px;
  width: 100%;
  height: 40px;
  border: none;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: linear-gradient(135deg, #2F54EB, #7C3AED);
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s, transform 0.15s;
}

.quota-buy-btn:hover {
  filter: brightness(1.08);
}

.quota-buy-btn:active {
  transform: scale(0.98);
}

.quota-buy-btn.ok {
  background: #16A34A;
}

.quota-byok {
  margin-top: 8px;
  text-align: center;
  font-size: 12px;
  color: #9CA3AF;
}

[data-theme="dark"] .quota-card {
  border-color: #7C2D12;
  background: linear-gradient(180deg, rgba(124, 45, 18, 0.28) 0%, #1C1C1E 100%);
  color: #F3F4F6;
}

[data-theme="dark"] .quota-card.switched {
  border-color: #166534;
  background: linear-gradient(180deg, rgba(22, 101, 52, 0.25) 0%, #1C1C1E 100%);
}

[data-theme="dark"] .quota-icon {
  background: rgba(234, 88, 12, 0.2);
  color: #FB923C;
}

[data-theme="dark"] .quota-icon.ok {
  background: rgba(22, 163, 74, 0.2);
  color: #4ADE80;
}

[data-theme="dark"] .quota-title-block span {
  color: #9CA3AF;
}

[data-theme="dark"] .quota-meter-label {
  color: #FDBA74;
}

[data-theme="dark"] .quota-meter-track {
  background: rgba(234, 88, 12, 0.2);
}

[data-theme="dark"] .quota-upsell {
  background: rgba(99, 102, 241, 0.14);
  color: #D1D5DB;
}

[data-theme="dark"] .quota-byok {
  color: #6B7280;
}
</style>
